import React from 'react';
import type { CSSProperties } from 'react';
import { pickOnPrimaryColor } from '@/themes/colorUtils';
import { opaqueContrastBaseOf } from '@/themes/surfaceOpacity';
// WEB-3447, заход 2: градиенты бренда — через дверь ленивого куска.
import { authorGradient, gradientTextColor } from '@/themes/styleKitDoor';
import { buttonBorderOverride } from './blockBorder';

/**
 * WEB-955 — фон-подложка текстового блока. Зеркало бэкенд-схемы
 * `block-schema/block-background.ts` (имена совпадают, реестр пар WEB-880
 * не нужен).
 */
export interface BlockBackground {
  style: 'none' | 'solid' | 'translucent' | 'blur' | 'glass';
  /** hex (#rgb / #rrggbb / #rrggbbaa) — валидируется бэком. */
  color?: string;
  /** 0..1 — плотность подложки для полупрозрачных стилей. */
  opacity?: number;
}

/**
 * Дефолтная плотность каждого пресета — ЕДИНСТВЕННОЕ место правды: её читают
 * и сборка стиля ниже, и слайдер «Плотность» в панели свойств (иначе панель
 * показывала бы одно, а рендер делал другое).
 */
export const DEFAULT_BLOCK_BG_OPACITY: Record<
  Exclude<BlockBackground['style'], 'none'>,
  number
> = {
  solid: 1,
  translucent: 0.6,
  blur: 0.35,
  glass: 0.25,
};

/**
 * hex → rgba(). Понимает #rgb / #rrggbb / #rrggbbaa; альфа-байт 8-значного
 * hex ПЕРЕМНОЖАЕТСЯ с запрошенной плотностью (а не выбрасывается — ревью
 * WEB-955 п.2: тихая усечка давала разный результат у solid и translucent).
 * Мусор на входе не роняет рендер, а падает в белый.
 */
/**
 * ЭКСПОРТИРУЕТСЯ (заказ владельца 01.09, плотность фона плашек feature_chips):
 * правда о превращении hex+плотность в цвет одна — вторая копия разошлась бы.
 */
export function hexToRgba(hex: string, rawAlpha: number): string {
  // WEB-3684: последняя защита — плотность, которая не конечное число
  // (undefined, NaN, строка), считается за 1. Иначе `rgba(…, NaN)`, браузер
  // значение выбрасывает, и заливка исчезает вовсе.
  const alpha = Number.isFinite(rawAlpha) ? rawAlpha : 1;
  const h = hex.replace('#', '');
  const base =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h.slice(0, 6);
  const n = Number.parseInt(base, 16);
  if (Number.isNaN(n) || base.length !== 6) {
    return `rgba(255, 255, 255, ${alpha})`;
  }
  const hexAlpha =
    h.length === 8 ? Number.parseInt(h.slice(6, 8), 16) / 255 : 1;
  const a = Math.round(alpha * (Number.isNaN(hexAlpha) ? 1 : hexAlpha) * 1000) / 1000;
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/**
 * BlockBackground с исключённым style:'none' — Exclude<BlockBackground, ...>
 * тут не сработал бы (BlockBackground не юнион вариантов, а один интерфейс с
 * широким полем style — Exclude не дистрибутивен по такому типу).
 */
type NonNoneBackground = Omit<BlockBackground, 'style'> & {
  style: Exclude<BlockBackground['style'], 'none'>;
};

/** Годный hex — то же правило, что у схемы сервера (`block-background.ts`). */
const BLOCK_BG_HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

function isFillStyle(style: unknown): style is NonNoneBackground['style'] {
  return (
    typeof style === 'string' &&
    Object.prototype.hasOwnProperty.call(DEFAULT_BLOCK_BG_OPACITY, style)
  );
}

/**
 * WEB-3684 — ОДНА дверь приёма поля фона блока. Тип поля обещает объект
 * `{ style, color, opacity }`, но данные воронки могут нести и строку
 * (`'#f0f0f0'`): у строки нет `style`, плотность выходила `undefined`, и
 * заливка собиралась `rgba(255, 255, 255, NaN)` — браузер её выбрасывал,
 * баннер срочности становился прозрачным.
 *
 * Возвращает ЗАЛИВКУ, которую надо рисовать, или undefined — «как будто поля
 * нет» (рендер идёт по ветке «поле не задано»):
 *  - объект с известным `style`, кроме 'none' — он сам; негодный цвет (не hex)
 *    и плотность, которая не конечное число, снимаются (цвет — в прежнее
 *    умолчание «белый», плотность — в умолчание пресета);
 *  - строка с годным hex — сплошная заливка этого цвета: намерение автора
 *    однозначно, и вид совпадает с тем, что дал бы правильный объект;
 *  - `style: 'none'`, неизвестный `style`, любая другая строка, число,
 *    массив, null — undefined.
 *
 * Все экспортированные функции файла, которые принимают поле фона, зовут её
 * первой; `blockInnerSurface.tsx` и `LoaderRenderer.tsx` — тоже её, без своей
 * проверки `.style !== 'none'` (вторая копия правила разошлась бы).
 */
export function toBlockBackground(raw: unknown): NonNoneBackground | undefined {
  if (typeof raw === 'string') {
    return BLOCK_BG_HEX.test(raw) ? { style: 'solid', color: raw } : undefined;
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return undefined;
  }
  const obj = raw as Record<string, unknown>;
  if (!isFillStyle(obj.style)) return undefined;
  const colorOk =
    obj.color === undefined ||
    (typeof obj.color === 'string' && BLOCK_BG_HEX.test(obj.color));
  const opacityOk =
    obj.opacity === undefined ||
    (typeof obj.opacity === 'number' && Number.isFinite(obj.opacity));
  if (colorOk && opacityOk) return raw as NonNoneBackground;
  return {
    style: obj.style,
    ...(colorOk && obj.color !== undefined ? { color: obj.color as string } : {}),
    ...(opacityOk && obj.opacity !== undefined
      ? { opacity: obj.opacity as number }
      : {}),
  };
}

/**
 * WEB-1440 — итоговый ЦВЕТ заливки (hex + плотность → rgba) отдельно от
 * остального стиля подложки. Нужен «поверхности элемента»
 * (blockInnerSurface.tsx): она красит не сам узел, а CSS-переменную
 * --color-bg-surface, и ей нужен ровно цвет — без паддинга, радиуса и блюра.
 * Правда о том, как hex+плотность превращаются в цвет, остаётся одна
 * (hexToRgba + DEFAULT_BLOCK_BG_OPACITY), как и у стилей ниже.
 */
export function backgroundFillColor(
  raw: BlockBackground | undefined,
): string | undefined {
  // WEB-3684: поле — через одну дверь; строка, null и чужая форма не доходят
  // до сборки цвета.
  const nonNone = toBlockBackground(raw);
  if (!nonNone) return undefined;
  return hexToRgba(
    nonNone.color ?? '#ffffff',
    nonNone.opacity ?? DEFAULT_BLOCK_BG_OPACITY[nonNone.style],
  );
}

/**
 * WEB-1859 — ЦВЕТ ТЕКСТА ПО ЭТОЙ ПОДЛОЖКЕ либо undefined, если подложка
 * плёнка и судить по ней не о чем.
 *
 * ОДНА точка ответа на вопрос «считаем ли текст по заливке» для кнопки, значка
 * гарантии и поверхности элемента (blockInnerSurface.tsx) — раньше все трое
 * звали `pickOnPrimaryColor(bg.color)` НАПРЯМУЮ, без всякого гейта, и потому
 * расходились и с помощником экрана (порог 0.9), и с пилюлей (0x80).
 *
 * В замер входит НЕ ТОЛЬКО альфа кода цвета, но и ПЛОТНОСТЬ пресета: та же
 * пара (цвет, плотность), из которой `hexToRgba` собирает саму заливку. Иначе
 * тёмный цвет при плотности «стекла» 0.25 давал белую надпись на светлой по
 * факту поверхности — известная дыра пресета с ползунком.
 *
 * undefined означает «пусть решает тема»: у темы, в отличие от плёнки, есть
 * знание о странице под ней. У кнопки это ровно третья ступень уже
 * задокументированного приоритета (`buttonSurfaceStyle`): ключ `color` не
 * появляется в стиле вовсе.
 */
export function fillContrastTextColor(
  raw: BlockBackground | undefined,
): string | undefined {
  const nonNone = toBlockBackground(raw);
  if (!nonNone) return undefined;
  // WEB-3684: дверь снимает негодный цвет (заливка падает в прежний белый),
  // но судить о цвете текста по нему нельзя — прежде такой цвет (например,
  // переменная темы) был «неизмеримым», и решала тема (WEB-1859). Цвет был
  // и снят — замера нет, как раньше.
  if (raw && (raw as { color?: unknown }).color !== undefined && nonNone.color === undefined) {
    return undefined;
  }
  const base = opaqueContrastBaseOf(
    nonNone.color ?? '#ffffff',
    nonNone.opacity ?? DEFAULT_BLOCK_BG_OPACITY[nonNone.style],
  );
  return base ? pickOnPrimaryColor(base) : undefined;
}

/**
 * Общая часть подложки (фон/размытие/рамка), не зависящая от того, красим ли
 * мы КАРТОЧКУ блока (blockBackgroundStyle — паддинг+радиус карточки) или САМУ
 * КНОПКУ (buttonBackgroundStyle — без паддинга/радиуса, у кнопки свои).
 * Единственная точка правды про то, как выглядит каждый пресет — обе функции
 * ниже зовут её, а не копируют свитч.
 */
function backgroundFillStyle(bg: NonNoneBackground): CSSProperties {
  const color = bg.color ?? '#ffffff';
  const alpha = bg.opacity ?? DEFAULT_BLOCK_BG_OPACITY[bg.style];
  const backgroundColor = hexToRgba(color, alpha);

  switch (bg.style) {
    case 'solid':
    case 'translucent':
      return { backgroundColor };
    case 'blur':
      return {
        backgroundColor,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      };
    case 'glass':
      return {
        backgroundColor,
        backdropFilter: 'blur(14px) saturate(1.4)',
        WebkitBackdropFilter: 'blur(14px) saturate(1.4)',
        border: '1px solid rgba(255, 255, 255, 0.35)',
      };
    default:
      return {};
  }
}

/**
 * Собрать inline-стиль подложки. ЕДИНСТВЕННАЯ точка правды о том, как каждый
 * пресет выглядит: её зовут и превью конструктора, и публичная страница через
 * общий PublicBlockRenderer (урок WEB-942 — никаких двух копий сборки стиля).
 *
 * Отсутствие поля / style 'none' → пустой объект: старые воронки не меняются
 * ни на пиксель.
 *
 * blur/glass строятся на backdrop-filter; браузер без его поддержки просто
 * игнорирует свойство, и остаётся полупрозрачная сплошная подложка — читаемость
 * сохраняется, теряется только эффект размытия.
 */
export interface BlockPaddingFields {
  /** WEB-1678 — внутренние отступы блока. Absent = прежние 10/10/14. */
  blockPaddingTop?: number;
  blockPaddingBottom?: number;
  blockPaddingX?: number;
}

/** Прежние зашитые числа (`padding: '10px 14px'`) — теперь как умолчание. */
export const DEFAULT_BLOCK_PADDING = { y: 10, x: 14 } as const;

/** Тронул ли автор хоть один отступ. Нет → ни пометки, ни лишней обёртки. */
export function hasBlockPadding(fields?: BlockPaddingFields): boolean {
  return (
    typeof fields?.blockPaddingTop === 'number' ||
    typeof fields?.blockPaddingBottom === 'number' ||
    typeof fields?.blockPaddingX === 'number'
  );
}

/**
 * WEB-1678 — отступы подложки. Раньше здесь стояло зашитое `10px 14px`, и
 * автору было нечем убрать зазор над текстом. Собираются четырьмя сторонами
 * (а не сокращённым `padding`), чтобы своя настройка одной стороны не
 * стирала умолчания трёх остальных.
 *
 * Умолчание зависит от того, есть ли ЗАЛИВКА: 10/10/14 — это отступы КАРТОЧКИ
 * («чтобы подложка выглядела карточкой, а не полосой впритык к тексту»). Без
 * заливки карточки нет и никаких отступов у блока раньше не было — значит
 * незаданная сторона там ноль, иначе правка одной стороны молча сдвинула бы
 * три остальных.
 */
export function resolveBlockPadding(
  fields: BlockPaddingFields | undefined,
  hasFill: boolean,
): CSSProperties {
  const fallbackY = hasFill ? DEFAULT_BLOCK_PADDING.y : 0;
  const x = fields?.blockPaddingX ?? (hasFill ? DEFAULT_BLOCK_PADDING.x : 0);
  return {
    paddingTop: fields?.blockPaddingTop ?? fallbackY,
    paddingBottom: fields?.blockPaddingBottom ?? fallbackY,
    paddingLeft: x,
    paddingRight: x,
  };
}

export function blockBackgroundStyle(
  raw?: BlockBackground,
  padding?: BlockPaddingFields,
): CSSProperties {
  const bg = toBlockBackground(raw);
  const hasFill = bg !== undefined;
  if (!hasFill && !hasBlockPadding(padding)) return {};

  // Без заливки автор красит не карточку, а только расстояние до содержимого:
  // радиус там нечему скруглять, и лишнее свойство в inline-стиле старой
  // воронки было бы шумом.
  const base: CSSProperties = hasFill
    ? {
        ...resolveBlockPadding(padding, true),
        borderRadius: 'var(--radius-card, 12px)',
      }
    : resolveBlockPadding(padding, false);
  return bg ? { ...base, ...backgroundFillStyle(bg) } : base;
}

/**
 * Запрос владельца продукта 10.08 — «Фон кнопки»: та же механика подложки,
 * что и «Фон блока» выше (переиспользует backgroundFillStyle, не копирует
 * свитч), но собрана ДЛЯ САМОЙ КНОПКИ, а не для карточки-обёртки:
 *  - без padding/borderRadius карточки — у кнопки уже есть свои (из
 *    primaryButtonStyle / собственного inline-стиля button_link);
 *  - `border: 'none'` для solid/translucent/blur — снимает обводку у
 *    outlined-варианта кнопки (button_link), когда фон всё равно красится;
 *    glass добавляет свою тонкую рамку сама (backgroundFillStyle);
 *  - цвет текста — через pickOnPrimaryColor(color), тот же контраст-хелпер,
 *    что уже красит --color-on-primary у тем (WEB-872) — иначе тёмный текст
 *    по умолчанию на тёмной пользовательской подложке становится нечитаем.
 *
 * Вызывающая сторона мержит результат ПОСЛЕ primaryButtonStyle(...) —
 * порядок ключей в объекте решает, чей backgroundColor/border победит в
 * итоговом style-атрибуте (стандартное поведение каскада CSS: позже
 * объявленное для того же свойства побеждает).
 */
export function buttonBackgroundStyle(raw?: BlockBackground): CSSProperties {
  const bg = toBlockBackground(raw);
  if (!bg) return {};
  const fill = backgroundFillStyle(bg);
  // WEB-1859: авто-контраст только по НЕПРОЗРАЧНОЙ подложке (fillContrastTextColor).
  const autoColor = fillContrastTextColor(raw);
  return {
    ...fill,
    border: bg.style === 'glass' ? fill.border : 'none',
    ...(autoColor ? { color: autoColor } : {}),
  };
}

/**
 * WEB-1651-2 — ЕДИНЫЙ приоритет вида кнопки для всех кнопочных мест обоих
 * рендереров (button_next / button_link / button_to_pay / stripe_checkout_form
 * / return_to_app_button / close_webview_button / hero CTA).
 *
 * Репорт PM: «фон/заливка кнопки» и «цвет текста внутри кнопки» из панели не
 * применяются. Цвет текста автор задаёт полем `innerTextColor` (секция
 * «Заливка элемента», доступна ЛЮБОМУ блоку), но оно попадало только на
 * ОБЁРТКУ блока (`withBlockInnerSurface` — CSS-переменные + color на
 * `<div data-block-inner-surface>`), а сама кнопка несла свой inline-color:
 * сперва из `primaryButtonStyle`, затем авто-контраст
 * `pickOnPrimaryColor(buttonBackground.color)`. Inline на самом элементе
 * сильнее унаследованного — выбор автора не побеждал никогда. Тот же класс,
 * что шрам 2026-08-03 (WEB-934): «добавил переменную в слой темы — проверь,
 * перекрывает ли её слой-победитель».
 *
 * Приоритеты:
 *  - заливка: `buttonBackground` («Фон кнопки», специальное поле) сильнее
 *    `innerBackground` («Заливка элемента», общее поле блока). Второе
 *    подхватывается, чтобы панельная секция визуально работала и на кнопках —
 *    у кнопочного блока «элемент» и есть кнопка;
 *  - цвет текста: `innerTextColor` (явный выбор) → авто-контраст по заливке
 *    (прежнее поведение buttonBackgroundStyle) → дефолт темы (поля нет —
 *    ключ `color` не появляется вовсе, старые воронки не меняются).
 *
 * Живёт ЗДЕСЬ, рядом с buttonBackgroundStyle, а не копией в семи case-ветках:
 * иначе следующая правка приоритета опять доедет не всюду (шрам WEB-880 —
 * «грепать ВСЕ рендер-пути потребителей»).
 */
export interface ButtonSurfaceFields {
  buttonBackground?: BlockBackground;
  innerBackground?: BlockBackground;
  innerTextColor?: string;
  /** WEB-1823 — рамка кнопки. Absent = как решит тема (см. buttonBorderOverride). */
  borderWidth?: number;
  borderColor?: string;
  borderRadius?: number;
  /**
   * Решение владельца №2 (2026-10-08) — градиент кнопки (закрытая форма,
   * themes/gradient.ts). Сильнее `buttonBackground`/`innerBackground`;
   * негодная строка — как будто поля нет.
   */
  buttonGradient?: string;
  /**
   * Слово владельца 21.10.2026 — свой цвет НАДПИСИ кнопки (button_next /
   * button_link). Сильнее `innerTextColor` и авто-контраста заливки и
   * градиента; пусто — как будто поля нет.
   */
  buttonTextColor?: string;
}

function activeBackground(raw?: BlockBackground): BlockBackground | undefined {
  // WEB-3684: негодная форма — «поля нет», и тогда в дело идёт следующее по
  // приоритету поле, а не пустая заливка. Отдаётся СЫРОЕ значение: сборщик
  // кнопки сам пропускает его через дверь, а замеру цвета текста нужен
  // исходный цвет (снятый негодный цвет — «неизмерим», WEB-1859).
  return toBlockBackground(raw) ? raw : undefined;
}

export function buttonSurfaceStyle(fields: ButtonSurfaceFields): CSSProperties {
  const fill =
    activeBackground(fields.buttonBackground) ??
    activeBackground(fields.innerBackground);
  const ownTextColor =
    fields.buttonTextColor?.trim() || fields.innerTextColor?.trim() || undefined;

  // WEB-1976: заливка отдаётся ключом `backgroundColor`, а тема для вида
  // 'gradient' кладёт СОКРАЩЁННОЕ `background: linear-gradient(...)` — это
  // background-image, и цвет под ним не виден. Гасим картинку фона ровно
  // тогда, когда автор задал свою заливку: без полей ключа нет вовсе и
  // градиентная тема остаётся сама собой. Здесь, в единственном сборщике, а не
  // копией в семи кнопочных ветках (шрам WEB-880).
  const base = fill
    ? { ...buttonBackgroundStyle(fill), backgroundImage: 'none' }
    : buttonBackgroundStyle(fill);
  // Решение владельца №2: градиент автора ложится ПОВЕРХ заливки картинкой
  // фона (backgroundImage — тот же ключ, которым выше гасится градиент темы),
  // текст — авто-контрастом по первому стопу, если свой цвет не задан.
  const gradient = authorGradient(fields.buttonGradient);
  const gradientColor = gradientTextColor(gradient);
  const withGradient: CSSProperties = gradient
    ? {
        ...base,
        backgroundImage: gradient,
        border: 'none',
        ...(gradientColor ? { color: gradientColor } : {}),
      }
    : base;
  const withText = ownTextColor ? { ...withGradient, color: ownTextColor } : withGradient;
  // WEB-1823: рамка автора кладётся ПОСЛЕДНЕЙ — заливка выше ставит
  // `border: 'none'`, и явный выбор автора обязан её перебить. На пустых полях
  // надстройка отдаёт {}, поэтому старые воронки не меняются ни на пиксель.
  return { ...withText, ...buttonBorderOverride(fields) };
}

/**
 * Запрос владельца продукта 10.08 — фон ВНУТРЕННЕЙ области значка гарантии
 * (guarantee_badge): та же механика подложки, что и «Фон кнопки» выше
 * (переиспользует backgroundFillStyle, не копирует свитч), но для внутренней
 * области значка, а не карточки-обёртки И не рамки:
 *  - без padding/borderRadius карточки — у значка уже есть свои
 *    (PublicBlockRendererExtended.tsx, case 'guarantee_badge');
 *  - БЕЗ принудительного border:'none' (в отличие от buttonBackgroundStyle) —
 *    рамка значка настраивается независимым полем badgeBorderColor, эта
 *    функция её не трогает; glass — единственное исключение и тут, и у
 *    кнопки: несёт свою тонкую рамку сама (backgroundFillStyle);
 *  - цвет текста/иконки — через pickOnPrimaryColor(color), тот же
 *    контраст-хелпер, что уже красит текст кнопки — иначе дефолтный
 *    var(--color-text-on-surface) на пользовательском тёмном фоне стал бы
 *    нечитаем (тот же класс проблемы, что WEB-966 решал для подложки самого
 *    значка).
 *
 * Вызывающая сторона мержит результат ПОСЛЕ базового inline-стиля значка —
 * тот же порядок ключей, что у withBlockBackground/buttonBackgroundStyle
 * выше решает, чей backgroundColor/color побеждает в итоговом style.
 */
export function badgeInnerBackgroundStyle(raw?: BlockBackground): CSSProperties {
  const bg = toBlockBackground(raw);
  if (!bg) return {};
  const fill = backgroundFillStyle(bg);
  // WEB-1859: тот же гейт непрозрачности, что у кнопки и поверхности элемента.
  const autoColor = fillContrastTextColor(raw);
  return {
    ...fill,
    ...(autoColor ? { color: autoColor } : {}),
  };
}

/**
 * WEB-1062 — «Фон блока» доступен ЛЮБОМУ типу, не только шести текстовым
 * (WEB-955). Раньше стиль мержился ВРУЧНУЮ в style каждого host-элемента
 * (h1/p/ul/ol) — работало только там, куда его скопировали, и не могло
 * дотянуться до кастомных компонентов (LoaderProcessingBlock и т.п.), у
 * которых нет пропа style. Вместо копипасты в ~70 case-веток общий рендер
 * оборачивает уже готовый узел ОДИН раз — в PublicBlockRenderer(Extended),
 * рядом с withBlockFont (тот же приём: обернуть готовый rendered).
 *
 * Нет фона / style='none' / нечего рендерить → узел возвращается БЕЗ
 * обёртки: старые воронки не получают лишний div и не меняются ни на
 * пиксель (WEB-1062 п.5 «в1 без поля — вид не изменился»).
 *
 * Overload-пара зеркалит withBlockFont (blockFont.tsx) — узкий вариант для
 * вызова из PublicBlockRendererExtended (rendered: ReactElement | null),
 * широкий — для PublicBlockRenderer (rendered может быть undefined,
 * делегирующим случаем).
 */
export function withBlockBackground(
  background: BlockBackground | undefined,
  node: React.ReactElement | null,
  padding?: BlockPaddingFields,
): React.ReactElement | null;
export function withBlockBackground(
  background: BlockBackground | undefined,
  node: React.ReactNode,
  padding?: BlockPaddingFields,
): React.ReactNode;
export function withBlockBackground(
  background: BlockBackground | undefined,
  node: React.ReactNode,
  padding?: BlockPaddingFields,
): React.ReactNode {
  // WEB-3684: та же дверь, что у сборки стиля — иначе обёртка с пометкой
  // появилась бы у поля, которое стиль считает пустым.
  const fill = toBlockBackground(background);
  const hasFill = fill !== undefined;
  const hasPadding = hasBlockPadding(padding);
  if ((!hasFill && !hasPadding) || node === null || node === undefined || node === false) {
    return node;
  }
  return (
    <div
      {...(fill ? { 'data-block-background': fill.style } : {})}
      // WEB-1678: пометка появляется ТОЛЬКО когда автор занялся отступами —
      // по ней global.css гасит верхнее поле содержимого (у `<h1>` оно от
      // UA-таблицы, 0.67em, и без этого ноль в настройке ничего бы не убрал).
      // Не тронул — атрибута нет, правило спит, старая воронка не двигается.
      {...(hasPadding ? { 'data-block-padding': '1' } : {})}
      style={blockBackgroundStyle(background, padding)}
    >
      {node}
    </div>
  );
}
